import * as THREE from 'three';
import { createFire, waver, type Fire } from '../environment/fire.js';
import { glowSprite } from '../environment/settlement-props.js';
import { conditions } from '../environment/conditions.js';
import { GRIP_ROLL, GRIP_TILT } from './character-motion.js';
import { weaponGripMount, weaponGripRadius } from './weapon-grip.js';
import type { CharacterRig } from './character-model.js';

/**
 * Full strength out to about 18 m and nothing past 36 m (approved: three times the usual 20 ft bright and 20 ft dim; the rules give no range).
 * The pool is the old 6 m / 12 m one scaled by three: the light hangs three times as high over the ground, at nine times the intensity, so the
 * ground and figures beside the bearer are lit as before, only over three times the distance, and the bearer's own chest is not burnt out.
 */
export const TORCH_LIGHT_RANGE = 36;
const TORCH_LIGHT_INTENSITY = 54;
/** How far over the flame the light hangs. */
const LIGHT_LIFT = 5.9;
const TORCH_LIGHT_COLOR = 0xff8a3c;
/** Haft length and how far of it runs below the fist; the head is the last `HEAD` of it. */
const HAFT = .56, BUTT = .12, HEAD = .13;

/** A hand torch: an ash haft, its head wrapped in pitch-soaked rag and bound with cord, built along -Y from the grip (the off-hand blade's frame). */
function torchProp(): { prop: THREE.Group; tip: THREE.Object3D; head: THREE.MeshStandardMaterial } {
  const wood = new THREE.MeshStandardMaterial({ color: 0x5a4433, roughness: .95 }), cord = new THREE.MeshStandardMaterial({ color: 0x6b5a40, roughness: 1 });
  const head = new THREE.MeshStandardMaterial({ color: 0x241b15, roughness: 1, emissive: 0xc2410c, emissiveIntensity: 0, flatShading: true });
  const prop = new THREE.Group(); prop.name = 'torch';
  const haft = new THREE.Mesh(new THREE.CylinderGeometry(.015, .019, HAFT - HEAD * .6, 7), wood); haft.position.y = BUTT - (HAFT - HEAD * .6) / 2;
  const wrap = new THREE.Mesh(new THREE.CylinderGeometry(.036, .027, HEAD, 8, 2), head); wrap.position.y = BUTT - HAFT + HEAD / 2;
  wrap.rotation.y = .4;
  prop.add(haft, wrap);
  for (const y of [BUTT - HAFT + HEAD * .85, BUTT - HAFT + HEAD * .2]) {
    const band = new THREE.Mesh(new THREE.TorusGeometry(y > BUTT - HAFT + HEAD / 2 ? .03 : .037, .005, 4, 12), cord); band.rotation.x = Math.PI / 2; band.position.y = y; prop.add(band);
  }
  prop.traverse(object => { if (object instanceof THREE.Mesh) { object.castShadow = true; object.receiveShadow = true; } });
  const tip = new THREE.Object3D(); tip.position.y = BUTT - HAFT + .02; prop.add(tip);
  return { prop, tip, head };
}

interface Torch { id: string; rig: CharacterRig; tip: THREE.Object3D; head: THREE.MeshStandardMaterial; fire: Fire; halo: THREE.Sprite; flame: THREE.Group; lit: number; seed: number; at: THREE.Vector3; output: number }

/**
 * Heroes' torches. The torch itself is held in the off hand (`HumanAnimator` poses the arm); its flame burns in world space at the torch's head,
 * so it always rises whatever the hand does. Torches burn at night and underground and are out by day; a felled hero's goes out.
 *
 * The light: every lit surface pays for every point light in the scene, so torches share a fixed pool (the Torch lights setting) given each
 * frame to the lit torches nearest the view; the rest keep their flame and glow. The pool's size changes only with the setting or the number
 * of torch bearers, which recompiles lit materials once.
 */
export class Torches {
  private readonly torches = new Map<string, Torch>();
  private readonly lights: THREE.PointLight[] = [];
  private limit = Infinity;
  private time?: number;
  constructor(private readonly scene: THREE.Scene) {}

  /** Puts a torch in the hand of a figure whose unit holds one. */
  add(id: string, rig: CharacterRig) {
    this.remove(id);
    const { prop, tip, head } = torchProp(), hand = rig.leftArm.hand, scale = rig.proportions.handScale;
    prop.position.copy(weaponGripMount(-1, scale, weaponGripRadius('torch'))); prop.rotation.set(-Math.PI / 2 + GRIP_TILT, GRIP_ROLL, 0);
    hand.add(prop); rig.root.updateWorldMatrix(true, true);
    // Sized in metres, whatever the hand's own scale.
    prop.scale.setScalar(1 / (hand.getWorldScale(new THREE.Vector3()).x || 1));
    const seed = [...id].reduce((sum, c) => sum + c.charCodeAt(0), 0) * .37;
    const fire = createFire({ size: .13, embers: 3, seed }), halo = glowSprite(0xff9a45, 1.4, .5), flame = new THREE.Group();
    halo.position.y = .12; flame.add(fire.group, halo); flame.visible = false; this.scene.add(flame);
    this.torches.set(id, { id, rig, tip, head, fire, halo, flame, lit: 0, seed, at: new THREE.Vector3(), output: 0 });
    this.fitPool();
  }
  remove(id: string) {
    const torch = this.torches.get(id); if (!torch) return;
    torch.flame.removeFromParent(); this.torches.delete(id); this.fitPool();
  }
  /** The most torches that may light the world at once (`Infinity` for all of them). */
  setLimit(limit: number) { this.limit = limit; this.fitPool(); }
  get lightCount() { return this.lights.length; }
  get lightObjects(): readonly THREE.PointLight[] { return this.lights; }
  private fitPool() {
    const count = Math.max(0, Math.min(this.limit, this.torches.size));
    while (this.lights.length < count) { const light = new THREE.PointLight(TORCH_LIGHT_COLOR, 0, TORCH_LIGHT_RANGE, 2); light.name = `torch-light-${this.lights.length}`; this.scene.add(light); this.lights.push(light); }
    while (this.lights.length > count) this.lights.pop()!.removeFromParent();
  }

  /**
   * `shown` says whether a unit's figure is drawn and standing (alive), `underground` whether it is in a cave; `focus` is the ground at the
   * centre of the view; `time` is the game's clock in seconds, so the flames hold still while it is paused;
   * `real` is the page's clock in seconds, over which torches catch and go out whatever the game's clock does. Call after the figures are posed.
   */
  update(time: number, real: number, shown: (id: string) => { visible: boolean; alive: boolean; underground: boolean } | undefined, focus: { x: number; z: number }) {
    const dt = THREE.MathUtils.clamp(real - (this.time ?? real), 0, .25); this.time = real;
    const night = THREE.MathUtils.smoothstep(conditions.night, .2, .6);
    const lit: Torch[] = [];
    for (const torch of this.torches.values()) {
      const unit = shown(torch.id), burning = unit?.alive ? Math.max(night, unit.underground ? 1 : 0) : 0;
      // Lit and put out over about a second rather than at once.
      torch.lit = THREE.MathUtils.clamp(torch.lit + Math.sign(burning - torch.lit) * Math.min(Math.abs(burning - torch.lit), dt * 1.2), 0, 1);
      torch.head.emissiveIntensity = torch.lit * (.8 + waver(time * 1.3, torch.seed) * .15);
      torch.flame.visible = !!unit?.visible && torch.lit > .01;
      torch.output = 0;
      if (!torch.flame.visible) continue;
      torch.tip.getWorldPosition(torch.at);
      torch.flame.position.copy(torch.at); torch.flame.scale.setScalar(.25 + .75 * torch.lit);
      const brightness = torch.fire.update(time);
      torch.halo.material.opacity = .45 * brightness * torch.lit;
      torch.output = torch.lit * (brightness + waver(time * 1.8, torch.seed + 2) * .07);
      lit.push(torch);
    }
    // The pool goes to the lit torches nearest the view's centre.
    lit.sort((a, b) => Math.hypot(a.at.x - focus.x, a.at.z - focus.z) - Math.hypot(b.at.x - focus.x, b.at.z - focus.z));
    this.lights.forEach((light, i) => {
      const torch = lit[i];
      if (!torch) { light.intensity = 0; return; }
      light.position.copy(torch.at).y += LIGHT_LIFT; light.intensity = TORCH_LIGHT_INTENSITY * torch.output;
    });
  }

  dispose() { for (const id of [...this.torches.keys()]) this.remove(id); this.limit = 0; this.fitPool(); }
}
